import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartOptions, ChartData } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { ChartSeries, ChartPeriod, ChartReferenceLine } from '../interfaces/chart-data.interface';
import { buildReferenceLineAnnotations } from '../utils/chart-helpers';

@Component({
  selector: 'app-wind-line-chart',
  imports: [BaseChartDirective],
  templateUrl: './wind-line-chart.html',
  styleUrl: './wind-line-chart.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WindLineChart {
  seriesData = input.required<ChartSeries[]>();
  period = input<ChartPeriod>('1d');
  referenceLines = input<ChartReferenceLine[]>([]);

  chartData = computed<ChartData<'line'>>(() => {
    const series = this.seriesData();
    if (series.length === 0) {
      return { datasets: [] };
    }

    const datasets = series.map(s => ({
      data: s.data.map(d => ({ x: d.timestamp.getTime(), y: d.value })),
      label: s.label,
      borderColor: s.color || '#9C27B0',
      backgroundColor: s.color || 'rgba(156, 39, 176, 0.3)',
      fill: true,
      tension: 0.1,
      pointRadius: 0,
    }));

    return { datasets };
  });

  chartOptions = computed<ChartOptions<'line'>>(() => {
    const annotations = buildReferenceLineAnnotations(this.referenceLines());
    const currentPeriod = this.period();
    const tooltipFormat = currentPeriod === '1d' ? 'HH:mm' : 'MM/dd HH:mm';

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        annotation: {
          annotations
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (context) => {
              const datasetIndex = context.datasetIndex;
              const dataIndex = context.dataIndex;
              const originalPoint = this.seriesData()[datasetIndex]?.data[dataIndex];
              const rawDirection = originalPoint?.extra?.['windDirection'];
              const direction = typeof rawDirection === 'string' ? ` (${rawDirection})` : '';
              const val = context.parsed.y;
              return `${context.dataset.label}: ${val} m/s${direction}`;
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            tooltipFormat,
            displayFormats: {
              minute: 'HH:mm',
              hour: 'HH:mm',
              day: 'MM/dd'
            }
          },
          display: true,
          grid: {
            display: false
          }
        },
        y: {
          display: true,
          beginAtZero: true,
          title: {
            display: true,
            text: '風速 (m/s)'
          }
        }
      }
    };
  });
}
